import {inject, Injectable} from '@angular/core';
import {FirebaseApiService} from './firebase-api.service';
import {LibSnackbarService} from "../../../../../tal-library/snackbar/lib-snackbar/common/service/lib-snackbar.service";
import {FireStoreSearchInDto, FirestoreWrapperService} from "../../../../../core/services/firestore-wrapper.service";
import {firstValueFrom, Observable, skip, Subscription} from "rxjs";
import {NotificationSnackbarComponent} from "../../notification-snackbar/notification-snackbar.component";
import {collection, collectionData, Firestore, query, QueryConstraint, where} from "@angular/fire/firestore";
import {SearchOutDto} from "../../../common/model/search.model";

@Injectable({
    providedIn: 'root'
})
export class NotificationService {

    private firestoreWrapperService = inject(FirestoreWrapperService);
    private libSnackbarService = inject(LibSnackbarService);
    private firestore = inject(Firestore);
    private readonly userNotification = 'user__notification';
    private readonly notificationUrl = 'notifications';
    private readonly STATUS = 'status';
    private readonly NAME = 'name';
    private readonly STRICT_EQUALITY_OPERATOR = '==';
    private readonly ADDED = 'added';
    private readonly READ = 'READ';
    private APPLICANT_ID = 'applicantId';

    constructor(private firebaseApiService: FirebaseApiService) {
    }

    listenForSnackbars(userId: string): Subscription {
        return this.firestoreWrapperService.listenAddEvent(this.getUrl(userId)).pipe(skip(1)).subscribe(changes => {
            changes.forEach(change => {
                const data = change.doc.data() as NotificationOutDto;
                if (change.type === this.ADDED && data.status === NOTIFICATION_STATUS.UNREAD) {
                    const notification = {...data, id: change.doc.id};
                    this.openSnackBar(notification);
                }
            });
        });
    }

    listenForRealtimeUpdates(userId: string): Observable<any> {
        return this.firestoreWrapperService.listenAddEvent(this.getUrl(userId));
    }

    getUnReadNotification(userId: string): Observable<NotificationOutDto[]> {
        const ref = collection(this.firestore, this.getUrl(userId));
        const unreadQuery = query(ref, where(this.STATUS, this.STRICT_EQUALITY_OPERATOR, NOTIFICATION_STATUS.UNREAD));
        return collectionData(unreadQuery, {idField: 'id'}) as Observable<NotificationOutDto[]>;
    }

    search(userId: string, notificationSearchInDto: NotificationSearchInDto): Observable<SearchOutDto<NotificationOutDto>> {
        const firestoreSearchDto = this.createFirestoreSearchInDto(notificationSearchInDto);
        return this.firestoreWrapperService.search<NotificationOutDto>(this.getUrl(userId), firestoreSearchDto);
    }

    async markAllAsRead(userId: string): Promise<void> {
        const unreadResult = await firstValueFrom(this.getUnReadNotification(userId));
        if (!unreadResult || unreadResult.length === 0) return;
        const updates = unreadResult.map(notification => ({id: notification.id, data: {status: this.READ}}));
        return this.firestoreWrapperService.batchUpdate(this.getUrl(userId), updates);
    }

    markOneAsRead(notificationId: string, userId: string): Promise<void> {
        return this.firestoreWrapperService.update(this.getUrl(userId), notificationId, {status: this.READ});
    }

    async clearAll(userId: string): Promise<void> {
        const allNotificationsResult = await firstValueFrom(this.firestoreWrapperService.listen<NotificationOutDto>(this.getUrl(userId)));
        if (!allNotificationsResult || allNotificationsResult?.length === 0) return;
        const ids = allNotificationsResult.map(notificationOutDto => notificationOutDto?.id);
        return this.firestoreWrapperService.batchDelete(this.getUrl(userId), ids);
    }

    getNotificationCount(userId: string) {
        return this.firebaseApiService.getNotificationCount(userId);
    }

    private openSnackBar(notification: NotificationOutDto) {
        this.libSnackbarService.openFromComponent(NotificationSnackbarComponent, {
            data: {data: notification, timeout: 5000},
            horizontalPosition: 'right',
            verticalPosition: 'bottom',
            duration: 5000,
            panelClass: 'notification-snackbar-container',
        });
    }

    private createFirestoreSearchInDto(notificationSearchInDto: NotificationSearchInDto) {
        const {status, applicantId, name, ...baseOptions} = notificationSearchInDto;
        const constraints: QueryConstraint[] = [];
        if (status) {
            constraints.push(where(this.STATUS, this.STRICT_EQUALITY_OPERATOR, status));
        }
        if (name) {
            constraints.push(where(this.NAME, this.STRICT_EQUALITY_OPERATOR, name));
        }
        const firestoreSearchDto: FireStoreSearchInDto = {
            ...baseOptions,
            constraints: [...(baseOptions.constraints || []), ...constraints],
        };
        return firestoreSearchDto;
    }


    private getUrl(userId: string): string {
        return `${this.userNotification}/${userId}/${this.notificationUrl}`;
    }

}

export class NotificationSearchInDto extends FireStoreSearchInDto {
    userId: string;
    status?: NOTIFICATION_STATUS;
    applicantId?: string;
    name?: string;
}

export class NotificationOutDto {
    id?: string;
    userId?: string;
    talentId?: string;
    applicantId?: string;
    jobPostId?: string;
    companyId?: string;
    universityId?: string;
    batchId?: string;
    placementDriveId?: string;
    conversationId?: string;
    title?: string;
    jobPostName?: string;
    applicantName?: string;
    applicantStatus?: string;
    currentApplicantStatus?: string;
    currentHiringRoundName?: string;
    companyName?: string;
    comment?: string;
    subject?: string;
    message?: string;
    universityName?: string;
    batchName?: string;
    placementDriveName?: string;
    universityLogoUrl?: string;
    userProfileImageUrl?: string;
    companyLogoUrl?: string;
    metadata?: NotificationMetadata;
    status?: NOTIFICATION_STATUS;
    type?: NOTIFICATION_TYPE;
    notificationEventType: NOTIFICATION_EVENT_TYPE;
}

export class NotificationMetadata {
    status?: string;
    description?: string;
}

export class UserNotificationOutDto {
    userId?: string;
    fullName?: string;
    emailAddress?: string;
    contactNumber?: string;
    profileUrl?: string;
    role?: string;
    company?: string;
}

export enum NOTIFICATION_STATUS {
    READ = 'READ',
    UNREAD = 'UNREAD'
}

export enum NOTIFICATION_TYPE {
    RELEASE = 'RELEASE'
}

export enum NOTIFICATION_EVENT_TYPE {
    UNIVERSITY_BROADCAST = 'UNIVERSITY_BROADCAST',
    PLACEMENT_DRIVE_INVITATION = 'PLACEMENT_DRIVE_INVITATION',
    APPLICATION_ACCEPTED = 'APPLICATION_ACCEPTED',
    APPLICATION_DECLINED = 'APPLICATION_DECLINED',
    CHAT_MESSAGE = 'CHAT_MESSAGE',
}

